/**
 * Evidence doc eligibility — ES3 / DAE3 pipeline gate.
 *
 * Single source of truth for whether user-uploaded documents participate
 * in SED bootstrap, doc-aware confidence, and report generation.
 * In `ai-only` mode docs may be stored in the library but are excluded
 * from all evidence pipelines until the user switches to docs-included.
 */
import type { SiteEntity } from '../types/site';
import type { SiteDocument } from '../types/siteRag';
import type { SiteContext } from '../types/siteContext';
import { isLibraryIndexDoc } from './knowledge/libraryIndexDocument';
import { isAiOnlyEvidenceMode } from './evidenceReadiness';

// RB3 — re-export the reference selection classifier so the references /
// appendix / DOCX evidence surfaces can distinguish "user-excluded" from
// "not auto-selected (below relevance floor)" through the evidence-doc
// module they already depend on. The threshold + classification logic
// live with the auto-recommend rule (single source of truth for the
// auto-include floor); this is a thin pass-through to avoid forcing every
// surface to reach into autoRecommendedRefs directly.
export {
  classifyRefSelection,
  isUserExcludedRef,
  isNotAutoSelectedRef,
  refSelectionLabel,
} from './autoRecommendedRefs';
export type { RefSelectionState, ClassifiableRef } from './autoRecommendedRefs';

export type EvidenceDocSite = Pick<SiteEntity, 'evidenceMode' | 'documents'>;

/** User docs that may flow into SED / reports. Empty when ai-only. */
export function docsEligibleForEvidence(site: EvidenceDocSite): SiteDocument[] {
  if (isAiOnlyEvidenceMode(site.evidenceMode)) return [];
  // deletedAt: soft-deleted docs stay in `site.documents` for restore/audit
  // but must never feed evidence — a rebuilt SED carried two just-deleted
  // docs through this gate into its library blocks (Redhead, 2026-07-28).
  return (site.documents ?? []).filter(
    d => !isLibraryIndexDoc(d) && d.includeInSed !== false && !d.deletedAt && d.archived !== true,
  );
}

/** Doc IDs for bootstrap `includeDocIds` — mirrors docsEligibleForEvidence. */
export function eligibleDocIdsForSed(site: EvidenceDocSite): string[] {
  return docsEligibleForEvidence(site).map(d => d.id);
}

/**
 * Current evidence view only; the canonical SiteContext retains every source
 * claim for history. Document merges retain the winning sourceDocId, so an
 * excluded winner cannot be rescued by a guessed secondary document. Reviewing
 * a document claim also does not turn it into independently authored evidence.
 * Legacy/user-typed claims and prose retain their existing authority.
 */
export function siteContextForEligibleDocuments(
  context: SiteContext | undefined,
  eligibleDocumentIds: ReadonlySet<string>,
): SiteContext | undefined {
  if (!context) return context;
  const removedClaimIds = new Set<string>();
  const claims = (context.claims ?? []).filter(claim => {
    const fromDocument = claim.source === 'document-extracted' || claim.source === 'merged';
    const eligible = !fromDocument || (
      typeof claim.sourceDocId === 'string'
      && claim.sourceDocId.length > 0
      && eligibleDocumentIds.has(claim.sourceDocId)
    );
    if (!eligible) removedClaimIds.add(claim.id);
    return eligible;
  });
  if (removedClaimIds.size === 0) return context;
  return {
    ...context,
    claims,
    // Stale document conflicts must not leak back through a second prompt lane.
    // Unmatched legacy/provider conflicts and all acknowledgement history stay.
    conflicts: (context.conflicts ?? []).filter(conflict => !removedClaimIds.has(conflict.claimId)),
  };
}
